import { TILE_ATTRIBUTES } from './tile-attributes';

/**
 * BROWSER PATH. `attributesToRetrieve` for the /search results page — the one listing that queries
 * Algolia from the browser.
 *
 * Why this exists: the page is backed by `algolia/cachedProductsSearchClient.ts`, which pulls the
 * whole result set ONCE per (index, query, filters) and then answers every refinement / sort / page
 * locally. That pull used to ask for `['*']`, because the local facet engine (`computeFacets`) needs the
 * facet attributes on each hit and the InstantSearch `Configure` only listed the short product-tile set.
 * Retrieving everything is enormously expensive on the search page: measured on `caroma-au-main` for
 * `query=car` (1,000 hits) the response was **16,651 KB**; asking for the tile fields plus the configured
 * facets brings it to **2,491 KB** (−85%).
 *
 * So the rule is the same one the server path uses: retrieve exactly "tile fields ∪ configured facets",
 * with the SAME tile fields (helpers/algolia/tile-attributes.ts). The search client honours whatever
 * `attributesToRetrieve` the request carries — sending only the tile fields would silently empty the
 * facet panel, since a facet cannot be counted from data that was never fetched.
 *
 * NOTE: the FACET paths here keep Algolia's `searchableAttributes.*` spelling, unlike the server path which
 * rewrites them to the canonical `attributes.*` copy. That is deliberate — this client reads RAW Algolia
 * records (its `collectValues` has no `searchableAttributes` redirect), and the Studio facet keys are
 * authored against that same spelling. The tile fields are canonical: mapProduct only reads `attributes`.
 */

/**
 * Tile fields ∪ the configured facet attribute keys, deduped. `facetsConfiguration` is the
 * `{ attributePath: { type, label } }` map the /search tastic builds from the Studio configuration,
 * so its KEYS are exactly the paths the facet UI will try to count.
 */
export const clientRetrieveAttributes = (facetsConfiguration?: Record<string, unknown>): string[] =>
  Array.from(new Set([...TILE_ATTRIBUTES, ...Object.keys(facetsConfiguration ?? {})]));
